import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useTheme } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { SyncPanel } from "@/components/sync/SyncPanel";
import NoticeBar from "./NoticeBar";
import ProductsHeader from "./ProductsHeader";
import ProductsFilterBar from "./ProductsFilterBar";
import ProductsGridView from "./ProductsGridView";
import ProductsListView from "./ProductsListView";
import GetStartedHero from "./GetStartedHero";
import { getProductContextMeshResults, getProductsList, requestProductContextMeshAnalysis, type ProductContextMeshResult } from "./api";
import type { DashboardSort, DashboardView, Product } from "./types";
import {
    discardProductCreationDraft,
    isProductStudioCreationDraft,
    listProductCreationDrafts,
    type ProductCreationDraftRecord
} from "../newProduct/draftApi";
import { ConfirmDialog } from "../newProduct/ConfirmDialog";
import { useProductBuildProgress } from "./useProductBuildProgress";
import { PRODUCT_BUILD_RETRY_SUCCEEDED_EVENT, useOptionalBackgroundActivity } from "../backgroundActivity";
import { useOptionalBatchGeneration, type BatchReadiness, type TrackedBatch } from "../batchGeneration";
import { isActiveProductBatch } from "./ProductBatchGenerationStatus";
import { retryProductBuildActivity } from "./buildApi";
import { toast } from "sonner";
import PendingCreationCard from "./PendingCreationCard";
import {
    cancelProductCreationOperation,
    clearProductCreationOperationId,
    isActiveProductCreationOperation,
    listProductCreationOperations,
    retryProductCreationOperation,
    type ProductCreationOperation
} from "../newProduct/productCreationOperationsApi";

const HERO_DISMISSED_KEY = "product-studio.get-started-hero.dismissed";
const CONTEXT_MESH_POLL_INTERVAL_MS = 5000;
const PRODUCT_CREATION_POLL_INTERVAL_MS = 2000;
const PRODUCT_LINK_REFRESH_INTERVAL_MS = 2000;
const PRODUCT_LINK_REFRESH_MAX_ATTEMPTS = 5;
const SUCCEEDED_OPERATION_VISIBILITY_MS = 10 * 60 * 1000;

const applyProductContextMeshResults = (products: Product[], results: ProductContextMeshResult[]): Product[] => {
    const byProduct = new Map(results.map(result => [result.productId, result]));
    return products.map(product => {
        const result = byProduct.get(product.id);
        return result ? {
            ...product,
            contextMeshAnalysis: result.analysis,
            contextMeshScore: result.analysis?.score ?? product.contextMeshScore,
            contextMeshStatus: result.status
        } : product;
    });
};

const mergeProductListPreservingContextMesh = (current: Product[], incoming: Product[], refreshProductIds?: ReadonlySet<string>): Product[] => {
    const currentById = new Map(current.map(product => [product.id, product]));
    return incoming.map(product => {
        const previous = currentById.get(product.id);
        if (refreshProductIds?.has(product.id)) {
            const buildState = product.build?.state;
            return {
                ...product,
                contextMeshAnalysis: null,
                contextMeshStatus:
                    buildState === "processing" || buildState === "completed"
                        ? "analyzing"
                        : buildState === "failed" || buildState === "not_started"
                          ? "unavailable"
                          : product.contextMeshStatus
            };
        }
        return previous
            ? {
                  ...product,
                  contextMeshAnalysis: previous.contextMeshAnalysis,
                  contextMeshStatus: previous.contextMeshStatus
              }
            : product;
    });
};

const isRecentlySucceededCreation = (operation: ProductCreationOperation, now = Date.now()): boolean => {
    if (operation.status !== "succeeded") return false;
    const timestamp = Date.parse(operation.updated_at || operation.created_at);
    return Number.isFinite(timestamp) && now - timestamp <= SUCCEEDED_OPERATION_VISIBILITY_MS;
};

interface ProductsDashboardProps {
    token?: string | null;
    refreshSignal?: number;
    onOpenProduct: (productId: string) => void;
    onCreateProduct: () => void;
    /** Open the new-product screen resuming the given saved draft. */
    onResumeDraft?: (draftId: number) => void;
    /**
     * Called after the user confirms deletion in the dialog. The promise should
     * resolve when the parent has finished its side-effects (Redux update,
     * server delete, etc.). The dashboard re-fetches its list after resolution.
     */
    onDeleteProduct: (productId: string) => void | Promise<void>;
    /**
     * Pencil entry point (KAN-1464): navigate to the refresh-product screen for
     * this product instead of opening the legacy UpdateProductModal in-place.
     */
    onEditProduct: (productId: number) => void;
    /** Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16). */
    onNavigateToIntegrations?: () => void;
    /** Open the app-wide processing-jobs hub, optionally focused on one processor job. */
    onOpenProcessingJob?: (jobId?: string | null) => void;
    /** Request made by another navigation surface (for example chat) to show a blocked build. */
    requestedBuildProductId?: string | null;
    onBuildRequestHandled?: () => void;
    onBuildStateChange?: () => void;
    asyncProductCreationEnabled?: boolean;
    idempotencyUserScope?: string | null;
    /** Seeds the just-accepted launch so its card renders before the first owner-scoped GET. */
    initialCreationOperation?: ProductCreationOperation | null;
    onInitialCreationOperationConsumed?: () => void;
    /** Persist a newly linked Product selection without blocking operation progress. */
    onCreationProjectLinked?: (projectId: number) => void;
}

const ProductsDashboard = ({
    token,
    refreshSignal,
    onOpenProduct,
    onCreateProduct,
    onResumeDraft,
    onDeleteProduct,
    onEditProduct,
    onNavigateToIntegrations,
    onOpenProcessingJob,
    requestedBuildProductId,
    onBuildRequestHandled,
    onBuildStateChange,
    asyncProductCreationEnabled = false,
    idempotencyUserScope = null,
    initialCreationOperation = null,
    onInitialCreationOperationConsumed,
    onCreationProjectLinked
}: ProductsDashboardProps) => {
    const theme = useTheme();
    const [view, setView] = useState<DashboardView>("grid");
    const [sort, setSort] = useState<DashboardSort>("updated");
    const [query, setQuery] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncProductId, setSyncProductId] = useState<number | null>(null);
    const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [localRefresh, setLocalRefresh] = useState(0);
    const [linkedProductRefresh, setLinkedProductRefresh] = useState(0);
    const [pendingBatchProductIds, setPendingBatchProductIds] = useState<Set<string>>(() => new Set());
    const [retryingProductIds, setRetryingProductIds] = useState<Set<string>>(() => new Set());
    const [retryErrorsByProductId, setRetryErrorsByProductId] = useState<Map<string, string>>(() => new Map());
    const [blockedReadiness, setBlockedReadiness] = useState<BatchReadiness | null>(null);
    const [blockedProduct, setBlockedProduct] = useState<Product | null>(null);
    const terminalRefreshInFlight = useRef(false);
    const terminalRefreshQueue = useRef<Set<string>>(new Set());
    const productsRef = useRef(products);
    productsRef.current = products;
    const contextMeshRequestVersions = useRef<Map<string, number>>(new Map());
    const contextMeshRefreshInFlight = useRef<Set<string>>(new Set());
    const [drafts, setDrafts] = useState<ProductCreationDraftRecord[]>([]);
    const [draftsHydrated, setDraftsHydrated] = useState(false);
    const [deletingDraft, setDeletingDraft] = useState<ProductCreationDraftRecord | null>(null);
    const [creationOperations, setCreationOperations] = useState<ProductCreationOperation[]>(() =>
        initialCreationOperation ? [initialCreationOperation] : []
    );
    const [retryingCreationIds, setRetryingCreationIds] = useState<Set<string>>(() => new Set());
    const [returningCreationIds, setReturningCreationIds] = useState<Set<string>>(() => new Set());
    const productLinkRefreshAttempts = useRef<Map<string, number>>(new Map());
    const clearedTerminalOperationIds = useRef<Set<string>>(new Set());
    const operationsAwaitingProject = useRef<Set<string>>(new Set());
    const linkedProjectNotifications = useRef<Set<string>>(new Set());
    const batchGeneration = useOptionalBatchGeneration();
    const backgroundActivity = useOptionalBackgroundActivity();
    const [heroDismissed, setHeroDismissed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(HERO_DISMISSED_KEY) === "1";
        } catch {
            return false;
        }
    });

    const mergeCreationOperations = useCallback((incoming: ProductCreationOperation[]) => {
        setCreationOperations(current => {
            const next = new Map(incoming.map(operation => [operation.id, operation]));
            // A just-accepted launch can briefly precede read-replica visibility.
            // Preserve only active seeded/current rows until the owner list catches up.
            for (const operation of current) {
                if (!next.has(operation.id) && isActiveProductCreationOperation(operation)) {
                    next.set(operation.id, operation);
                }
            }
            return Array.from(next.values()).sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at));
        });
    }, []);

    useEffect(() => {
        if (!asyncProductCreationEnabled || !initialCreationOperation) return;
        setCreationOperations(current => {
            const next = new Map(current.map(operation => [operation.id, operation]));
            next.set(initialCreationOperation.id, initialCreationOperation);
            return Array.from(next.values());
        });
        onInitialCreationOperationConsumed?.();
    }, [asyncProductCreationEnabled, initialCreationOperation, onInitialCreationOperationConsumed]);

    useEffect(() => {
        for (const operation of creationOperations) {
            if (operation.project_id == null) {
                operationsAwaitingProject.current.add(operation.id);
                continue;
            }
            if (operationsAwaitingProject.current.has(operation.id) && !linkedProjectNotifications.current.has(operation.id)) {
                linkedProjectNotifications.current.add(operation.id);
                operationsAwaitingProject.current.delete(operation.id);
                onCreationProjectLinked?.(operation.project_id);
            }
        }
    }, [creationOperations, onCreationProjectLinked]);

    useEffect(() => {
        if (!asyncProductCreationEnabled || !token) {
            setCreationOperations([]);
            return;
        }
        let cancelled = false;
        listProductCreationOperations(token)
            .then(operations => {
                if (!cancelled) mergeCreationOperations(operations);
            })
            .catch(operationError => {
                if (!cancelled) setError(operationError instanceof Error ? operationError.message : "Failed to load product creation activity");
            });
        return () => {
            cancelled = true;
        };
    }, [asyncProductCreationEnabled, mergeCreationOperations, token]);

    const activeCreationKey = creationOperations
        .filter(isActiveProductCreationOperation)
        .map(operation => `${operation.id}:${operation.status}:${operation.phase}`)
        .sort()
        .join("|");

    useEffect(() => {
        if (!asyncProductCreationEnabled || !token || !activeCreationKey) return;
        let cancelled = false;
        let timeoutId: number | undefined;
        const poll = async () => {
            try {
                const operations = await listProductCreationOperations(token);
                if (cancelled) return;
                mergeCreationOperations(operations);
                timeoutId = window.setTimeout(poll, PRODUCT_CREATION_POLL_INTERVAL_MS);
            } catch {
                if (!cancelled) timeoutId = window.setTimeout(poll, PRODUCT_CREATION_POLL_INTERVAL_MS);
            }
        };
        timeoutId = window.setTimeout(poll, PRODUCT_CREATION_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, [activeCreationKey, asyncProductCreationEnabled, mergeCreationOperations, token]);

    const reserveContextMeshRequests = useCallback((targets: Product[]): Map<string, number> => {
        const versions = new Map<string, number>();
        for (const product of targets) {
            const version = (contextMeshRequestVersions.current.get(product.id) ?? 0) + 1;
            contextMeshRequestVersions.current.set(product.id, version);
            versions.set(product.id, version);
        }
        return versions;
    }, []);

    const applyLatestContextMeshResults = useCallback((results: ProductContextMeshResult[], versions: ReadonlyMap<string, number>) => {
        const latest = results.filter(result => contextMeshRequestVersions.current.get(result.productId) === versions.get(result.productId));
        if (latest.length > 0) {
            setProducts(current => applyProductContextMeshResults(current, latest));
        }
    }, []);

    const handleDismissHero = () => {
        setHeroDismissed(true);
        try {
            localStorage.setItem(HERO_DISMISSED_KEY, "1");
        } catch {
            // localStorage unavailable — dismissal still applies for this session
        }
    };

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getProductsList(token, sort)
            .then(items => {
                if (cancelled) return null;
                setProducts(current => mergeProductListPreservingContextMesh(current, items));
                setLoading(false);
                // New list responses carry enough summary data to render the
                // Product Hub without one analysis request per Product. Keep a
                // compatibility fallback only for older payloads explicitly
                // mapped as `loading`.
                const targets = items.filter(product => product.contextMeshStatus === "loading");
                if (targets.length === 0) return null;
                const versions = reserveContextMeshRequests(targets);
                return getProductContextMeshResults(token, targets).then(results => ({ results, versions }));
            })
            .then(payload => {
                if (cancelled || !payload) return;
                const { results, versions } = payload;
                applyLatestContextMeshResults(results, versions);
            })
            .catch((err: Error) => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [applyLatestContextMeshResults, reserveContextMeshRequests, token, sort, refreshSignal, localRefresh, linkedProductRefresh]);

    useEffect(() => {
        if (!asyncProductCreationEnabled || loading) return;
        const productIds = new Set(products.map(product => product.id));
        const candidates = creationOperations.filter(operation => {
            if (operation.project_id == null || productIds.has(String(operation.project_id))) {
                productLinkRefreshAttempts.current.delete(operation.id);
                return false;
            }
            if (!isActiveProductCreationOperation(operation) && !isRecentlySucceededCreation(operation)) return false;
            return (productLinkRefreshAttempts.current.get(operation.id) ?? 0) < PRODUCT_LINK_REFRESH_MAX_ATTEMPTS;
        });
        if (candidates.length === 0) return;
        const timeoutId = window.setTimeout(() => {
            for (const operation of candidates) {
                productLinkRefreshAttempts.current.set(operation.id, (productLinkRefreshAttempts.current.get(operation.id) ?? 0) + 1);
            }
            setLinkedProductRefresh(value => value + 1);
        }, PRODUCT_LINK_REFRESH_INTERVAL_MS);
        return () => window.clearTimeout(timeoutId);
    }, [asyncProductCreationEnabled, creationOperations, loading, products]);

    useEffect(() => {
        setDraftsHydrated(false);
    }, [token]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        listProductCreationDrafts(token)
            .then(items => {
                if (!cancelled) setDrafts(items.filter(isProductStudioCreationDraft));
            })
            .catch(err => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load product drafts");
            })
            .finally(() => {
                if (!cancelled) setDraftsHydrated(true);
            });
        return () => {
            cancelled = true;
        };
    }, [token, refreshSignal, localRefresh]);

    const handleConfirmDeleteDraft = async () => {
        if (!token || !deletingDraft) return;
        const draft = deletingDraft;
        setDeletingDraft(null);
        try {
            await discardProductCreationDraft(token, draft.draft_id, { cleanupStaged: true });
            setDrafts(current => current.filter(item => item.draft_id !== draft.draft_id));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete product draft");
        }
    };

    const replaceCreationOperation = useCallback((updated: ProductCreationOperation) => {
        setCreationOperations(current => {
            const index = current.findIndex(operation => operation.id === updated.id);
            if (index < 0) return [updated, ...current];
            return current.map(operation => (operation.id === updated.id ? updated : operation));
        });
    }, []);

    const handleRetryCreation = useCallback(
        async (operation: ProductCreationOperation) => {
            if (!token || !operation.retryable) return;
            setRetryingCreationIds(current => new Set(current).add(operation.id));
            try {
                const updated = await retryProductCreationOperation(token, operation.id);
                productLinkRefreshAttempts.current.delete(operation.id);
                replaceCreationOperation(updated);
            } catch (retryError) {
                setError(retryError instanceof Error ? retryError.message : "Product creation could not be retried");
            } finally {
                setRetryingCreationIds(current => {
                    const next = new Set(current);
                    next.delete(operation.id);
                    return next;
                });
            }
        },
        [replaceCreationOperation, token]
    );

    const handleReturnCreationToDraft = useCallback(
        async (operation: ProductCreationOperation) => {
            if (!token || operation.project_id != null || !operation.can_return_to_draft || !onResumeDraft) return;
            setError(null);
            setReturningCreationIds(current => new Set(current).add(operation.id));
            try {
                const canceled = await cancelProductCreationOperation(token, operation.id);
                replaceCreationOperation(canceled);
                if (idempotencyUserScope) {
                    clearProductCreationOperationId(idempotencyUserScope, canceled.draft_id, canceled.draft_version);
                }
                clearedTerminalOperationIds.current.add(canceled.id);
                onResumeDraft(canceled.draft_id);
            } catch (cancelError) {
                setError(cancelError instanceof Error ? cancelError.message : "Product creation could not return to draft");
            } finally {
                setReturningCreationIds(current => {
                    const next = new Set(current);
                    next.delete(operation.id);
                    return next;
                });
            }
        },
        [idempotencyUserScope, onResumeDraft, replaceCreationOperation, token]
    );

    const handleEditProduct = (product: Product) => {
        const numericId = parseInt(product.id, 10);
        if (!Number.isNaN(numericId)) {
            onEditProduct(numericId);
        }
    };

    const handleSyncProduct = (product: Product) => {
        const numericId = parseInt(product.id, 10);
        if (!Number.isNaN(numericId)) {
            setSyncProductId(numericId);
        }
    };

    const handleAnalyzeContext = useCallback(
        async (product: Product) => {
            if (
                !token ||
                product.contextMeshStatus !== "unavailable" ||
                (product.build != null && product.build.state !== "completed") ||
                contextMeshRefreshInFlight.current.has(product.id)
            ) {
                return;
            }

            const versions = reserveContextMeshRequests([product]);
            const requestVersion = versions.get(product.id);
            contextMeshRefreshInFlight.current.add(product.id);
            setProducts(current =>
                current.map(item => (item.id === product.id ? { ...item, contextMeshAnalysis: null, contextMeshStatus: "analyzing" } : item))
            );
            try {
                await requestProductContextMeshAnalysis(token, product.id);
            } catch (requestError) {
                if (contextMeshRequestVersions.current.get(product.id) === requestVersion) {
                    setProducts(current =>
                        current.map(item => (item.id === product.id ? { ...item, contextMeshAnalysis: null, contextMeshStatus: "unavailable" } : item))
                    );
                }
                toast.error(requestError instanceof Error ? requestError.message : "Context analysis could not be started. Try again shortly.");
            } finally {
                contextMeshRefreshInFlight.current.delete(product.id);
            }
        },
        [reserveContextMeshRequests, token]
    );

    const batchesByProductId = useMemo(() => {
        const result = new Map<string, TrackedBatch>();
        for (const batch of backgroundActivity?.batches ?? []) {
            if (!batch.handle) continue;
            for (const target of batch.handle.targets) {
                const productId = String(target.productId);
                const current = result.get(productId);
                const candidateActive = isActiveProductBatch(batch);
                const currentActive = isActiveProductBatch(current);
                const candidateTime = Date.parse(batch.handle.updatedAt || batch.createdAt) || 0;
                const currentTime = current ? Date.parse(current.handle?.updatedAt || current.createdAt) || 0 : 0;
                if (!current || (candidateActive && !currentActive) || (candidateActive === currentActive && candidateTime >= currentTime)) {
                    result.set(productId, batch);
                }
            }
        }
        return result;
    }, [backgroundActivity?.batches]);

    const markBatchPending = useCallback((productId: string, pending: boolean) => {
        setPendingBatchProductIds(current => {
            const next = new Set(current);
            if (pending) next.add(productId);
            else next.delete(productId);
            return next;
        });
    }, []);

    const handleGenerateAll = useCallback(
        async (product: Product) => {
            if (
                !batchGeneration ||
                product.cardState === "processing" ||
                pendingBatchProductIds.has(product.id) ||
                isActiveProductBatch(batchesByProductId.get(product.id))
            )
                return;

            const productId = Number(product.id);
            if (!Number.isInteger(productId) || productId <= 0) {
                setError("This Product does not have a valid generation target.");
                return;
            }

            markBatchPending(product.id, true);
            setError(null);
            try {
                const readiness = await batchGeneration.checkReadiness([productId], { trackChat: false });
                if (!readiness.ready) {
                    setBlockedProduct(product);
                    setBlockedReadiness(readiness);
                    return;
                }
                await batchGeneration.startBatch({
                    productIds: [productId],
                    origin: "product-hub-ui"
                });
            } catch (requestError) {
                const readiness = (requestError as Error & { readiness?: BatchReadiness }).readiness;
                if (readiness && !readiness.ready) {
                    setBlockedProduct(product);
                    setBlockedReadiness(readiness);
                } else {
                    setError(requestError instanceof Error ? requestError.message : "Unable to start batch generation.");
                }
            } finally {
                markBatchPending(product.id, false);
            }
        },
        [batchGeneration, batchesByProductId, markBatchPending, pendingBatchProductIds]
    );

    const closeReadinessDialog = () => {
        setBlockedReadiness(null);
        setBlockedProduct(null);
    };

    const editBlockedProduct = () => {
        const product = blockedProduct;
        closeReadinessDialog();
        if (product) handleEditProduct(product);
    };

    const openBatchDetails = useCallback(() => {
        backgroundActivity?.openForJob();
    }, [backgroundActivity]);

    const handleRequestDelete = (product: Product) => {
        setDeletingProduct(product);
    };

    const handleCancelDelete = () => {
        if (isDeleting) return;
        setDeletingProduct(null);
    };

    const handleConfirmDelete = async () => {
        if (!deletingProduct) return;
        setIsDeleting(true);
        setError(null);
        try {
            await onDeleteProduct(deletingProduct.id);
            setDeletingProduct(null);
            setLocalRefresh(n => n + 1);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete product");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleBuildTerminal = useCallback(
        async (productId: string) => {
            if (!token) return;
            terminalRefreshQueue.current.add(productId);
            if (terminalRefreshInFlight.current) return;
            terminalRefreshInFlight.current = true;
            try {
                // Serialize terminal revalidation without dropping another Product
                // that finishes while the current list request is in flight.
                while (terminalRefreshQueue.current.size > 0) {
                    const refreshProductIds = new Set(terminalRefreshQueue.current);
                    terminalRefreshQueue.current.clear();
                    const items = await getProductsList(token, sort);
                    const previousById = new Map(productsRef.current.map(product => [product.id, product]));
                    for (const item of items) {
                        if (item.build?.state === "completed" && previousById.get(item.id)?.build?.state !== "completed") {
                            refreshProductIds.add(item.id);
                        }
                    }
                    setProducts(current => mergeProductListPreservingContextMesh(current, items, refreshProductIds));
                    setError(null);
                    onBuildStateChange?.();
                    const targets = items.filter(product => refreshProductIds.has(product.id));
                    const versions = reserveContextMeshRequests(targets);
                    const contextMeshResults = await getProductContextMeshResults(token, targets);
                    applyLatestContextMeshResults(contextMeshResults, versions);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to refresh terminal Product build");
            } finally {
                terminalRefreshInFlight.current = false;
            }
        },
        [applyLatestContextMeshResults, onBuildStateChange, reserveContextMeshRequests, sort, token]
    );

    const handleRetryBuild = useCallback(
        async (product: Product) => {
            if (!token || product.build?.state !== "failed" || product.build.retryable !== true) return;
            const projectId = Number(product.id);
            if (!Number.isInteger(projectId) || projectId <= 0) {
                setRetryErrorsByProductId(current => new Map(current).set(product.id, "This Product does not have a valid retry target."));
                return;
            }

            setRetryingProductIds(current => new Set(current).add(product.id));
            setRetryErrorsByProductId(current => {
                const next = new Map(current);
                next.delete(product.id);
                return next;
            });
            try {
                const activityType = product.build.retry_activity_type?.trim() || "base_generation";
                const build = await retryProductBuildActivity(projectId, activityType, token);
                setProducts(current =>
                    current.map(item =>
                        item.id === product.id
                            ? {
                                  ...item,
                                  build,
                                  cardState: build.state === "completed" ? "standard" : "processing"
                              }
                            : item
                    )
                );
                onBuildStateChange?.();
                setLocalRefresh(value => value + 1);
            } catch (retryError) {
                const message = retryError instanceof Error ? retryError.message : "Unable to retry this Product build.";
                setRetryErrorsByProductId(current => new Map(current).set(product.id, message));
            } finally {
                setRetryingProductIds(current => {
                    const next = new Set(current);
                    next.delete(product.id);
                    return next;
                });
            }
        },
        [onBuildStateChange, token]
    );

    useEffect(() => {
        const handleBuildRetry = (event: Event) => {
            const productId = event instanceof CustomEvent ? event.detail?.productId : null;
            if (productId && products.some(product => product.id === String(productId))) {
                void handleBuildTerminal(String(productId));
            }
        };
        window.addEventListener(PRODUCT_BUILD_RETRY_SUCCEEDED_EVENT, handleBuildRetry);
        return () => window.removeEventListener(PRODUCT_BUILD_RETRY_SUCCEEDED_EVENT, handleBuildRetry);
    }, [handleBuildTerminal, products]);

    const analyzingProducts = useMemo(
        () => products.filter(product => product.build?.state !== "processing" && product.contextMeshStatus === "analyzing"),
        [products]
    );
    const analyzingProductKey = analyzingProducts
        .map(product => `${product.id}:${product.build?.state ?? "none"}`)
        .sort()
        .join("|");

    useEffect(() => {
        if (!token || analyzingProducts.length === 0) return;
        let cancelled = false;
        let timeoutId: number | undefined;

        const poll = async () => {
            const versions = reserveContextMeshRequests(analyzingProducts);
            try {
                const results = await getProductContextMeshResults(token, analyzingProducts);
                if (!cancelled) applyLatestContextMeshResults(results, versions);
            } catch {
                // Preserve the analyzing state and retry. Per-product API errors
                // are normally returned as `unavailable` results instead.
            }
            if (!cancelled) timeoutId = window.setTimeout(poll, CONTEXT_MESH_POLL_INTERVAL_MS);
        };

        timeoutId = window.setTimeout(poll, CONTEXT_MESH_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
        // The key deliberately tracks identity without resetting the timer for
        // unrelated Product-card updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyzingProductKey, applyLatestContextMeshResults, reserveContextMeshRequests, token]);

    const liveBuilds = useProductBuildProgress(products, token, handleBuildTerminal);
    const displayProducts = useMemo(
        () =>
            products.map(product => {
                const live = liveBuilds[product.id];
                const correlatedLive = live && live.processor_job_id === product.build?.processor_job_id ? live : null;
                // Live polling enriches active progress only. Once Intelligenic has
                // persisted a terminal build, that durable state must win over any
                // older hook snapshot (for example feature_import still "running").
                const persistedTerminal = product.build?.state === "completed" || product.build?.state === "failed";
                const build = persistedTerminal ? product.build : (correlatedLive ?? product.build);
                return {
                    ...product,
                    build,
                    cardState: build && build.state !== "completed" ? ("processing" as const) : ("standard" as const)
                };
            }),
        [liveBuilds, products]
    );
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return displayProducts;
        return displayProducts.filter(product => [product.name, product.sub, product.ownerName].some(value => value?.toLowerCase().includes(q)));
    }, [displayProducts, query]);
    const visibleCreationOperations = useMemo(() => {
        const q = query.trim().toLowerCase();
        const productIdsWithBuild = new Set(displayProducts.filter(product => product.build != null).map(product => product.id));
        return creationOperations.filter(operation => {
            // A succeeded operation has crossed the durable ProductBuild
            // handoff. It must never return as a pending card while the
            // canonical Product list is loading after dashboard remount.
            if (operation.status === "canceled" || operation.status === "succeeded") return false;
            // Product provisioning precedes build registration. Keep the
            // creation card while only the Product exists so registration
            // failures remain retryable, but hand off immediately once the
            // canonical ProductBuild is visible instead of waiting for the
            // next operation-status poll.
            if (operation.project_id != null && productIdsWithBuild.has(String(operation.project_id))) return false;
            return !q || operation.working_name.toLowerCase().includes(q);
        });
    }, [creationOperations, displayProducts, query]);
    const visibleDrafts = useMemo(() => {
        const operationDraftIds = new Set(creationOperations.filter(operation => operation.status !== "canceled").map(operation => operation.draft_id));
        return drafts.filter(
            draft => isProductStudioCreationDraft(draft) && !operationDraftIds.has(draft.draft_id)
        );
    }, [creationOperations, drafts]);
    const hydratedCreationOperations = draftsHydrated ? visibleCreationOperations : [];

    useEffect(() => {
        const realProductIds = new Set(displayProducts.map(product => product.id));
        for (const operation of creationOperations) {
            if (operation.status === "canceled" && !clearedTerminalOperationIds.current.has(operation.id) && idempotencyUserScope) {
                clearProductCreationOperationId(idempotencyUserScope, operation.draft_id, operation.draft_version);
                clearedTerminalOperationIds.current.add(operation.id);
                continue;
            }
            if (
                operation.status === "succeeded" &&
                operation.project_id != null &&
                realProductIds.has(String(operation.project_id)) &&
                !clearedTerminalOperationIds.current.has(operation.id) &&
                idempotencyUserScope
            ) {
                clearProductCreationOperationId(idempotencyUserScope, operation.draft_id, operation.draft_version);
                clearedTerminalOperationIds.current.add(operation.id);
            }
        }
    }, [creationOperations, displayProducts, idempotencyUserScope]);

    const openOrInspectProduct = useCallback(
        (product: Product) => {
            if (product.cardState === "processing" && product.build) {
                onOpenProcessingJob?.(product.build.processor_job_id);
                return;
            }
            onOpenProduct(product.id);
        },
        [onOpenProcessingJob, onOpenProduct]
    );

    useEffect(() => {
        if (!requestedBuildProductId) return;
        const product = displayProducts.find(item => item.id === requestedBuildProductId);
        if (!product) {
            if (!loading) onBuildRequestHandled?.();
            return;
        }
        if (product?.cardState === "processing" && product.build) {
            onOpenProcessingJob?.(product.build.processor_job_id);
        }
        onBuildRequestHandled?.();
    }, [displayProducts, loading, onBuildRequestHandled, onOpenProcessingJob, requestedBuildProductId]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                height: "100%",
                minHeight: 0,
                background: theme.palette.grey[50]
            }}
        >
            <NoticeBar />

            <Box
                sx={{
                    px: { xs: 2.5, md: 3.5 },
                    pt: 2.75,
                    pb: 1.5,
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}
            >
                <Stack spacing={2}>
                    <ProductsHeader totalCount={products.length + hydratedCreationOperations.length} />
                    <ProductsFilterBar query={query} onQueryChange={setQuery} sort={sort} onSortChange={setSort} view={view} onViewChange={setView} />
                </Stack>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    px: { xs: 2.5, md: 3.5 },
                    py: 2.5
                }}
            >
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {!heroDismissed && <GetStartedHero onStartProduct={onCreateProduct} onDismiss={handleDismissHero} />}
                {view === "list" && hydratedCreationOperations.length > 0 && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 1.75,
                            mb: 2
                        }}
                    >
                        {hydratedCreationOperations.map(operation => (
                            <PendingCreationCard
                                key={operation.id}
                                operation={operation}
                                onRetry={handleRetryCreation}
                                onReturnToDraft={handleReturnCreationToDraft}
                                retrying={retryingCreationIds.has(operation.id)}
                                returningToDraft={returningCreationIds.has(operation.id)}
                            />
                        ))}
                    </Box>
                )}
                {!draftsHydrated || (loading && !error && hydratedCreationOperations.length === 0) ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : view === "grid" ? (
                    <ProductsGridView
                        products={filtered}
                        onOpenProduct={openOrInspectProduct}
                        onEditProduct={handleEditProduct}
                        onDeleteProduct={handleRequestDelete}
                        onSyncProduct={handleSyncProduct}
                        onGenerateAll={batchGeneration ? handleGenerateAll : undefined}
                        onAnalyzeContext={handleAnalyzeContext}
                        onRetryBuild={handleRetryBuild}
                        retryingProductIds={retryingProductIds}
                        retryErrorsByProductId={retryErrorsByProductId}
                        onOpenBatchDetails={backgroundActivity ? openBatchDetails : undefined}
                        batchesByProductId={batchesByProductId}
                        pendingBatchProductIds={pendingBatchProductIds}
                        onCreateProduct={onCreateProduct}
                        creationOperations={hydratedCreationOperations}
                        onRetryCreation={handleRetryCreation}
                        onReturnCreationToDraft={handleReturnCreationToDraft}
                        retryingCreationIds={retryingCreationIds}
                        returningCreationIds={returningCreationIds}
                        drafts={visibleDrafts}
                        onResumeDraft={onResumeDraft ? draft => onResumeDraft(draft.draft_id) : undefined}
                        onDeleteDraft={setDeletingDraft}
                    />
                ) : (
                    <ProductsListView
                        products={filtered}
                        onOpenProduct={openOrInspectProduct}
                        onEditProduct={handleEditProduct}
                        onDeleteProduct={handleRequestDelete}
                        onSyncProduct={handleSyncProduct}
                        onGenerateAll={batchGeneration ? handleGenerateAll : undefined}
                        onAnalyzeContext={handleAnalyzeContext}
                        onRetryBuild={handleRetryBuild}
                        retryingProductIds={retryingProductIds}
                        retryErrorsByProductId={retryErrorsByProductId}
                        onOpenBatchDetails={backgroundActivity ? openBatchDetails : undefined}
                        batchesByProductId={batchesByProductId}
                        pendingBatchProductIds={pendingBatchProductIds}
                        onCreateProduct={onCreateProduct}
                        drafts={visibleDrafts}
                        onResumeDraft={onResumeDraft ? draft => onResumeDraft(draft.draft_id) : undefined}
                        onDeleteDraft={setDeletingDraft}
                    />
                )}
            </Box>

            {syncProductId !== null && (
                <SyncPanel
                    open
                    onClose={() => setSyncProductId(null)}
                    scope={{ type: "product", id: syncProductId }}
                    onNavigateToIntegrations={onNavigateToIntegrations}
                />
            )}

            <Dialog open={Boolean(blockedReadiness)} onClose={closeReadinessDialog} aria-labelledby="batch-readiness-title" maxWidth="sm" fullWidth>
                <DialogTitle id="batch-readiness-title">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningAmberRoundedIcon color="warning" />
                        Context Mesh needs attention
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Complete these inputs before generating all Work Products.
                    </Typography>
                    {blockedReadiness?.products.map(productReadiness => (
                        <Box key={productReadiness.productId} sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 700 }}>{productReadiness.productName}</Typography>
                            <Box component="ul" sx={{ mt: 0.75, mb: 0, pl: 2.5 }}>
                                {productReadiness.missingInputs.map(input => (
                                    <Typography component="li" key={input} sx={{ mb: 0.5, fontSize: 13.5 }}>
                                        {input}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReadinessDialog}>Close</Button>
                    <Button variant="contained" onClick={editBlockedProduct} disabled={!blockedProduct}>
                        Edit product
                    </Button>
                </DialogActions>
            </Dialog>

            {deletingDraft && (
                <ConfirmDialog
                    open
                    title="Delete draft?"
                    message={`Delete the draft "${deletingDraft.draft_state.state.fields.name.trim() || "Untitled product"}"? This cannot be undone.`}
                    confirmLabel="Delete draft"
                    cancelLabel="Keep draft"
                    destructive
                    onConfirm={() => void handleConfirmDeleteDraft()}
                    onCancel={() => setDeletingDraft(null)}
                />
            )}

            <Dialog
                open={Boolean(deletingProduct)}
                onClose={handleCancelDelete}
                aria-labelledby="delete-product-title"
                aria-describedby="delete-product-description"
            >
                <DialogTitle id="delete-product-title">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningAmberRoundedIcon color="warning" />
                        Delete Product
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography id="delete-product-description">
                        Are you sure you want to delete{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {deletingProduct?.name || "this product"}
                        </Box>
                        ? This action cannot be undone. All associated features and work products will be permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductsDashboard;
